// components/EnquiryPopup.jsx — ADVANCED MULTI-STEP ✅
import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import emailjs from "@emailjs/browser";


const steps = ["Contact Info", "Campaign Details", "Budget & Timeline"];


const platformOptions = [
    { name: "Google Ads", color: "#4285F4" },
    { name: "Meta (FB/IG)", color: "#0668E1" },
    { name: "TikTok Ads", color: "#000" },
    { name: "LinkedIn Ads", color: "#0A66C2" },
    { name: "YouTube Ads", color: "#FF0000" },
    { name: "X (Twitter)", color: "#000" },
    { name: "Snapchat Ads", color: "#FFFC00" },
    { name: "Pinterest Ads", color: "#E60023" },
    { name: "Amazon Ads", color: "#FF9900" },
    { name: "Microsoft Ads", color: "#00BCF2" },
];


const goalOptions = [
    "Lead Generation",
    "Brand Awareness",
    "E-commerce Sales",
    "App Downloads",
    "Website Traffic",
    "Video Views",
    "Local Store Visits",
    "Retargeting",
];


const budgetOptions = [
    "₹50K – ₹1 Lakh/mo",
    "₹1L – ₹5 Lakh/mo",
    "₹5L – ₹15 Lakh/mo",
    "₹15L – ₹50 Lakh/mo",
    "₹50 Lakh+/mo",
    "Not sure yet",
];


const timelineOptions = [
    "Immediately",
    "Within 1 week",
    "Within 2 weeks",
    "Within a month",
    "Just exploring",
];


const EnquiryPopup = ({ isOpen, onClose }) => {
    const [step, setStep] = useState(0);
    const [dir, setDir] = useState(1);
    const [submitted, setSubmitted] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState("");
    const [form, setForm] = useState({
        name: "", email: "", phone: "", company: "", website: "",
        platforms: [], goals: [], budget: "", timeline: "", message: "",
    });


    const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
    const toggleArr = (k, v) => setForm(p => ({
        ...p,
        [k]: p[k].includes(v) ? p[k].filter(x => x !== v) : [...p[k], v],
    }));


    const next = () => { setDir(1); setStep(s => Math.min(s + 1, 2)); };
    const prev = () => { setDir(-1); setStep(s => Math.max(s - 1, 0)); };


    const canNext = () => {
        if (step === 0) return form.name && form.phone && form.email;
        if (step === 1) return form.platforms.length > 0 && form.goals.length > 0;
        return true;
    };


    const handleSubmit = async () => {
        setIsSubmitting(true);
        setError("");

        // Prepare template params for EmailJS
        const templateParams = {
            from_name: form.name,
            from_email: form.email,
            phone: form.phone,
            subject: "Campaign Enquiry - Multi-Step Form",
            company: form.company || "Not provided",
            website: form.website || "Not provided",
            platforms: form.platforms.join(", ") || "Not selected",
            goals: form.goals.join(", ") || "Not selected",
            budget: form.budget || "Not specified",
            timeline: form.timeline || "Not specified",
            message: form.message || "No additional notes",
            to_name: "Adsurg Team",
        };

        const backendData = {
            name: form.name,
            email: form.email,
            phone: form.phone,
            company: form.company,
            website: form.website,
            platforms: form.platforms,
            goals: form.goals,
            budget: form.budget,
            timeline: form.timeline,
            message: form.message,
            type: "ENQUIRY",
        };

        try {
            // 1. Save to Backend FIRST (Ensures admin gets the query even if EmailJS fails)
            try {
                // Using fetch here to avoid breaking imports
                const fetchRes = await fetch(process.env.REACT_APP_API_URL ? `${process.env.REACT_APP_API_URL}/api/contacts` : "http://localhost:5000/api/contacts", {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify(backendData),
                });

                if (fetchRes.ok) {
                    console.log("✅ Backend Save SUCCESS");
                } else {
                    console.error("❌ Backend Save Error:", await fetchRes.text());
                }
            } catch (backendErr) {
                console.error("❌ Backend Request Error:", backendErr);
            }

            // 2. Send email via EmailJS (wrapped in its own block so failure doesn't stop UI)
            try {
                const emailResponse = await emailjs.send(
                    "service_1hmotcr",
                    "template_zjid6xh",
                    templateParams,
                    {
                        publicKey: "VWAvmxQ2rX99OLBpi",
                    }
                );
                console.log("✅ EmailJS SUCCESS:", emailResponse.status, emailResponse.text);
            } catch (emailErr) {
                console.error("❌ EmailJS Error (Ignoring to allow submission):", emailErr);
            }

            // 3. Complete Submission UI
            setSubmitted(true);

            // Auto-close after splash animation
            setTimeout(() => {
                setSubmitted(false);
                setForm({
                    name: "", email: "", phone: "", company: "", website: "",
                    platforms: [], goals: [], budget: "", timeline: "", message: ""
                });
                setStep(0);
                setError("");
                onClose();
            }, 3500);

        } catch (err) {
            console.error("❌ Global Submission Error:", err);
            setError("Failed to submit enquiry. Please try again.");
        } finally {
            setIsSubmitting(false);
        }
    };


    const close = () => {
        setStep(0);
        setSubmitted(false);
        setError("");
        onClose();
    };


    const variants = {
        enter: (d) => ({ x: d > 0 ? 60 : -60, opacity: 0 }),
        center: { x: 0, opacity: 1 },
        exit: (d) => ({ x: d > 0 ? -60 : 60, opacity: 0 }),
    };


    return (
        <>
            {/* ══════ FULL-SCREEN SUCCESS SPLASH ══════ */}
            <AnimatePresence>
                {submitted && (
                    <motion.div
                        className="fixed inset-0 z-[100] flex flex-col items-center justify-center"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.3 }}
                    >
                        {/* Dark backdrop */}
                        <motion.div
                            className="absolute inset-0 bg-gradient-to-b from-gray-950 via-gray-900 to-gray-950"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ duration: 0.4 }}
                        />


                        {/* Radial glow */}
                        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                            <motion.div
                                className="w-[500px] h-[500px] rounded-full"
                                style={{ background: 'radial-gradient(circle, rgba(34,197,94,0.15) 0%, transparent 70%)' }}
                                initial={{ scale: 0, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                transition={{ delay: 0.2, duration: 0.8, ease: 'easeOut' }}
                            />
                        </div>


                        {/* Content */}
                        <div className="relative z-10 flex flex-col items-center">
                            {/* Circle + Checkmark */}
                            <div className="relative w-32 h-32 sm:w-40 sm:h-40 mb-8">
                                <svg className="w-full h-full" viewBox="0 0 120 120">
                                    {/* Background ring */}
                                    <circle cx="60" cy="60" r="54" fill="none" stroke="rgba(34,197,94,0.15)" strokeWidth="5" />
                                    {/* Animated ring */}
                                    <motion.circle
                                        cx="60" cy="60" r="54" fill="none" stroke="#22c55e" strokeWidth="5"
                                        strokeLinecap="round"
                                        initial={{ pathLength: 0 }}
                                        animate={{ pathLength: 1 }}
                                        transition={{ duration: 0.7, ease: 'easeOut', delay: 0.2 }}
                                        style={{ rotate: -90, transformOrigin: 'center' }}
                                    />
                                    {/* Fill */}
                                    <motion.circle
                                        cx="60" cy="60" r="50"
                                        initial={{ scale: 0, opacity: 0 }}
                                        animate={{ scale: 1, opacity: 1 }}
                                        transition={{ delay: 0.6, duration: 0.4, type: 'spring', damping: 15 }}
                                        fill="rgba(34,197,94,0.1)"
                                    />
                                </svg>


                                {/* Checkmark */}
                                <motion.div
                                    className="absolute inset-0 flex items-center justify-center"
                                    initial={{ scale: 0, opacity: 0 }}
                                    animate={{ scale: 1, opacity: 1 }}
                                    transition={{ delay: 0.7, type: 'spring', damping: 10, stiffness: 200 }}
                                >
                                    <svg className="w-16 h-16 sm:w-20 sm:h-20" viewBox="0 0 24 24" fill="none">
                                        <motion.path
                                            d="M5 13l4 4L19 7"
                                            stroke="#22c55e"
                                            strokeWidth={2.5}
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            initial={{ pathLength: 0 }}
                                            animate={{ pathLength: 1 }}
                                            transition={{ delay: 0.8, duration: 0.5, ease: 'easeOut' }}
                                        />
                                    </svg>
                                </motion.div>


                                {/* Sparkle particles */}
                                {[0, 45, 90, 135, 180, 225, 270, 315].map((angle, i) => (
                                    <motion.div
                                        key={angle}
                                        className="absolute rounded-full"
                                        style={{
                                            width: i % 2 === 0 ? 8 : 5,
                                            height: i % 2 === 0 ? 8 : 5,
                                            background: i % 2 === 0 ? '#22c55e' : '#4ade80',
                                            top: '50%', left: '50%',
                                        }}
                                        initial={{ x: 0, y: 0, scale: 0, opacity: 0 }}
                                        animate={{
                                            x: Math.cos(angle * Math.PI / 180) * (80 + (i % 3) * 15),
                                            y: Math.sin(angle * Math.PI / 180) * (80 + (i % 3) * 15),
                                            scale: [0, 1.5, 0],
                                            opacity: [0, 1, 0],
                                        }}
                                        transition={{ delay: 0.9 + i * 0.05, duration: 0.7, ease: 'easeOut' }}
                                    />
                                ))}


                                {/* Ring pulse */}
                                <motion.div
                                    className="absolute inset-0 rounded-full border-2 border-emerald-400/40"
                                    initial={{ scale: 1, opacity: 0.6 }}
                                    animate={{ scale: 1.8, opacity: 0 }}
                                    transition={{ delay: 0.9, duration: 1, ease: 'easeOut' }}
                                />
                                <motion.div
                                    className="absolute inset-0 rounded-full border border-emerald-500/20"
                                    initial={{ scale: 1, opacity: 0.4 }}
                                    animate={{ scale: 2.2, opacity: 0 }}
                                    transition={{ delay: 1.1, duration: 1.2, ease: 'easeOut' }}
                                />
                            </div>


                            {/* Text */}
                            <motion.h2
                                className="text-white text-3xl sm:text-4xl font-black mb-3 text-center"
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 1.1, duration: 0.5 }}
                            >
                                Enquiry Submitted!
                            </motion.h2>
                            <motion.p
                                className="text-white/50 text-sm sm:text-base text-center max-w-sm leading-relaxed mb-2"
                                initial={{ opacity: 0, y: 15 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 1.3, duration: 0.5 }}
                            >
                                Thank you, <span className="text-emerald-400 font-semibold">{form.name}</span>!<br />
                                Our team will reach out within <span className="text-white font-semibold">24 hours</span>.
                            </motion.p>
                            <motion.p
                                className="text-white/30 text-xs"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ delay: 1.5, duration: 0.5 }}
                            >
                                {form.platforms.length} platform{form.platforms.length !== 1 ? 's' : ''} · {form.goals.length} goal{form.goals.length !== 1 ? 's' : ''}
                            </motion.p>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>


            {/* ══════ FORM MODAL ══════ */}
            <AnimatePresence>
                {isOpen && !submitted && (
                    <>
                        {/* Overlay */}
                        <motion.div
                            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[60]"
                            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                            onClick={close}
                        />


                        {/* Modal */}
                        <motion.div
                            initial={{ opacity: 0, scale: 0.9, y: 30 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.9, y: 30 }}
                            transition={{ type: "spring", damping: 25, stiffness: 300 }}
                            className="fixed inset-0 flex items-center justify-center z-[70] px-4 py-6"
                        >
                            <div
                                className="bg-white w-full max-w-lg rounded-2xl shadow-2xl shadow-black/20 relative overflow-hidden max-h-[90dvh] flex flex-col"
                                onClick={e => e.stopPropagation()}
                            >
                                {/* Header */}
                                <div className="bg-gradient-to-r from-gray-900 to-gray-800 px-6 py-5 relative overflow-hidden flex-shrink-0">
                                    <div className="absolute inset-0 bg-[#00809D]/10" />
                                    <div className="relative flex items-center justify-between">
                                        <div>
                                            <h2 className="text-white font-bold text-lg">Start Your Campaign</h2>
                                            <p className="text-white/50 text-xs mt-0.5">Fill in the details & we'll get back within 24hrs</p>
                                        </div>
                                        <button onClick={close} className="text-white/40 hover:text-white p-1.5 rounded-lg hover:bg-white/10 transition-all">
                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                                            </svg>
                                        </button>
                                    </div>


                                    {/* Step indicator */}
                                    <div className="relative mt-4 flex items-center gap-2">
                                        {steps.map((s, i) => (
                                            <div key={i} className="flex items-center gap-2 flex-1">
                                                <div className={`flex items-center gap-1.5 transition-all ${i <= step ? 'opacity-100' : 'opacity-40'}`}>
                                                    <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold border-2 transition-all ${i < step ? 'bg-[#00809D] border-[#00809D] text-white'
                                                        : i === step ? 'border-[#00809D] text-[#00809D] bg-[#00809D]/10'
                                                            : 'border-white/20 text-white/40'
                                                        }`}>
                                                        {i < step ? (
                                                            <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth={3} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                                                        ) : i + 1}
                                                    </div>
                                                    <span className={`text-[10px] font-semibold hidden sm:block ${i <= step ? 'text-white/80' : 'text-white/30'}`}>{s}</span>
                                                </div>
                                                {i < 2 && <div className={`flex-1 h-px transition-colors ${i < step ? 'bg-[#00809D]' : 'bg-white/10'}`} />}
                                            </div>
                                        ))}
                                    </div>
                                </div>


                                {/* Body */}
                                <div className="flex-1 overflow-y-auto">
                                    {/* Error message */}
                                    {error && (
                                        <motion.div
                                            initial={{ opacity: 0, y: -10 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            className="mx-6 mt-4 p-3 rounded-xl bg-red-50 border border-red-200 flex items-center gap-2"
                                        >
                                            <svg className="w-4 h-4 text-red-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                            </svg>
                                            <p className="text-red-700 font-medium text-xs">{error}</p>
                                        </motion.div>
                                    )}

                                    <AnimatePresence mode="wait" custom={dir}>
                                        <motion.div
                                            key={step}
                                            custom={dir}
                                            variants={variants}
                                            initial="enter"
                                            animate="center"
                                            exit="exit"
                                            transition={{ duration: 0.25, ease: "easeInOut" }}
                                            className="p-6 space-y-4"
                                        >
                                            {/* ── Step 1: Contact Info ── */}
                                            {step === 0 && (
                                                <>
                                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                                        <Field label="Full Name *" placeholder="John Doe" value={form.name} onChange={v => set('name', v)} />
                                                        <Field label="Email *" type="email" placeholder="john@company.com" value={form.email} onChange={v => set('email', v)} />
                                                    </div>
                                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                                        <Field label="Phone *" type="tel" placeholder="+91 98765 43210" value={form.phone} onChange={v => set('phone', v)} />
                                                        <Field label="Company Name" placeholder="Acme Inc." value={form.company} onChange={v => set('company', v)} />
                                                    </div>
                                                    <Field label="Website URL" placeholder="https://yourwebsite.com" value={form.website} onChange={v => set('website', v)} />
                                                </>
                                            )}


                                            {/* ── Step 2: Campaign Details ── */}
                                            {step === 1 && (
                                                <>
                                                    <div>
                                                        <label className="block text-gray-700 text-sm font-semibold mb-2.5">Select Platforms *</label>
                                                        <div className="grid grid-cols-2 gap-2">
                                                            {platformOptions.map((p, i) => {
                                                                const sel = form.platforms.includes(p.name);
                                                                return (
                                                                    <button key={i} type="button" onClick={() => toggleArr('platforms', p.name)}
                                                                        className={`flex items-center gap-2 px-3 py-2.5 rounded-xl border-2 text-left text-sm font-medium transition-all ${sel ? 'border-[#00809D] bg-[#00809D]/5 text-[#00809D]' : 'border-gray-200 text-gray-600 hover:border-gray-300 hover:bg-gray-50'
                                                                            }`}>
                                                                        <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: p.color }} />
                                                                        <span className="truncate text-xs sm:text-sm">{p.name}</span>
                                                                        {sel && <svg className="w-3.5 h-3.5 ml-auto text-[#00809D] flex-shrink-0" fill="none" stroke="currentColor" strokeWidth={3} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>}
                                                                    </button>
                                                                );
                                                            })}
                                                        </div>
                                                    </div>


                                                    <div>
                                                        <label className="block text-gray-700 text-sm font-semibold mb-2.5">Campaign Goals *</label>
                                                        <div className="flex flex-wrap gap-2">
                                                            {goalOptions.map((g, i) => {
                                                                const sel = form.goals.includes(g);
                                                                return (
                                                                    <button key={i} type="button" onClick={() => toggleArr('goals', g)}
                                                                        className={`px-3 py-1.5 rounded-lg border-2 text-xs sm:text-sm font-medium transition-all ${sel ? 'border-[#00809D] bg-[#00809D]/5 text-[#00809D]' : 'border-gray-200 text-gray-500 hover:border-gray-300'
                                                                            }`}>
                                                                        {g}
                                                                    </button>
                                                                );
                                                            })}
                                                        </div>
                                                    </div>
                                                </>
                                            )}


                                            {/* ── Step 3: Budget & Timeline ── */}
                                            {step === 2 && (
                                                <>
                                                    <div>
                                                        <label className="block text-gray-700 text-sm font-semibold mb-2.5">Monthly Budget</label>
                                                        <div className="grid grid-cols-2 gap-2">
                                                            {budgetOptions.map((b, i) => {
                                                                const sel = form.budget === b;
                                                                return (
                                                                    <button key={i} type="button" onClick={() => set('budget', b)}
                                                                        className={`px-3 py-2.5 rounded-xl border-2 text-xs sm:text-sm font-medium transition-all text-left ${sel ? 'border-[#00809D] bg-[#00809D]/5 text-[#00809D]' : 'border-gray-200 text-gray-600 hover:border-gray-300 hover:bg-gray-50'
                                                                            }`}>
                                                                        {sel && <span className="mr-1">✓</span>}{b}
                                                                    </button>
                                                                );
                                                            })}
                                                        </div>
                                                    </div>


                                                    <div>
                                                        <label className="block text-gray-700 text-sm font-semibold mb-2.5">When do you want to start?</label>
                                                        <div className="flex flex-wrap gap-2">
                                                            {timelineOptions.map((t, i) => {
                                                                const sel = form.timeline === t;
                                                                return (
                                                                    <button key={i} type="button" onClick={() => set('timeline', t)}
                                                                        className={`px-3 py-1.5 rounded-lg border-2 text-xs sm:text-sm font-medium transition-all ${sel ? 'border-[#00809D] bg-[#00809D]/5 text-[#00809D]' : 'border-gray-200 text-gray-500 hover:border-gray-300'
                                                                            }`}>
                                                                        {t}
                                                                    </button>
                                                                );
                                                            })}
                                                        </div>
                                                    </div>


                                                    <div>
                                                        <label className="block text-gray-700 text-sm font-semibold mb-2">Additional Notes</label>
                                                        <textarea
                                                            rows={3}
                                                            placeholder="Tell us about your business, target audience, current ad spend, or anything else..."
                                                            value={form.message}
                                                            onChange={e => set('message', e.target.value)}
                                                            className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-700 placeholder-gray-400 focus:outline-none focus:border-[#00809D] focus:ring-2 focus:ring-[#00809D]/10 transition-all resize-none"
                                                        />
                                                    </div>
                                                </>
                                            )}
                                        </motion.div>
                                    </AnimatePresence>
                                </div>


                                {/* Footer */}
                                <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between flex-shrink-0 bg-gray-50">
                                    {step > 0 ? (
                                        <button onClick={prev} className="flex items-center gap-1.5 text-gray-500 hover:text-gray-800 font-medium text-sm transition-colors">
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" /></svg>
                                            Back
                                        </button>
                                    ) : (
                                        <button onClick={close} className="text-gray-400 hover:text-gray-600 font-medium text-sm transition-colors">
                                            Maybe later
                                        </button>
                                    )}


                                    {step < 2 ? (
                                        <button
                                            onClick={next}
                                            disabled={!canNext()}
                                            className={`flex items-center gap-2 px-5 py-2.5 rounded-xl font-semibold text-sm transition-all ${canNext()
                                                ? 'bg-gray-900 hover:bg-gray-800 text-white shadow-md shadow-gray-900/10 hover:scale-[1.03] active:scale-[0.98]'
                                                : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                                }`}
                                        >
                                            Continue
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg>
                                        </button>
                                    ) : (
                                        <button
                                            onClick={handleSubmit}
                                            disabled={isSubmitting}
                                            className="flex items-center gap-2 bg-[#00809D] hover:bg-[#006d87] text-white px-6 py-2.5 rounded-xl font-bold text-sm shadow-lg shadow-[#00809D]/20 hover:shadow-xl hover:shadow-[#00809D]/30 hover:scale-[1.03] active:scale-[0.98] transition-all disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:scale-100"
                                        >
                                            {isSubmitting ? (
                                                <>
                                                    <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                                    </svg>
                                                    Sending...
                                                </>
                                            ) : (
                                                <>
                                                    Submit Enquiry
                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" /></svg>
                                                </>
                                            )}
                                        </button>
                                    )}
                                </div>
                            </div>
                        </motion.div>
                    </>
                )}
            </AnimatePresence>
        </>
    );
};


// Reusable input field
function Field({ label, type = "text", placeholder, value, onChange }) {
    return (
        <div>
            <label className="block text-gray-700 text-sm font-semibold mb-1.5">{label}</label>
            <input
                type={type}
                placeholder={placeholder}
                value={value}
                onChange={e => onChange(e.target.value)}
                className="w-full border-2 border-gray-200 rounded-xl px-4 py-2.5 text-sm text-gray-700 placeholder-gray-400 focus:outline-none focus:border-[#00809D] focus:ring-2 focus:ring-[#00809D]/10 transition-all"
            />
        </div>
    );
}


export default EnquiryPopup;
